import React, { useEffect, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import { IconTrash, IconStar, IconPhotoPlus } from '@tabler/icons';
import { encodePathSegments } from '../../../../../utils/media-url';

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:8000/api/';

export const IMOVEL_IMAGENS_MAX = 20;

// Editor de fotos do imóvel - combina as fotos já salvas (só existem no
// modo edição, com botão de remover/marcar capa) com as fotos novas
// staged localmente (ainda não enviadas, com preview via
// URL.createObjectURL) - mesmo espírito de `CreateProducts`/
// `handleImageUpload`, adaptado pra um imóvel só (sem variações).
const ImovelPhotosEditor = ({ appId, existingImages = [], onRemoveExisting, onSetPrincipal, newFiles, onNewFilesChange }) => {
    const previews = useMemo(() => newFiles.map((file) => URL.createObjectURL(file)), [newFiles]);

    useEffect(() => {
        return () => previews.forEach((url) => URL.revokeObjectURL(url));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previews]);

    const totalAtual = existingImages.length + newFiles.length;
    const podeAdicionar = totalAtual < IMOVEL_IMAGENS_MAX;

    const handleFilesSelected = (fileList) => {
        const files = Array.from(fileList || []);
        const espacoRestante = IMOVEL_IMAGENS_MAX - totalAtual;
        onNewFilesChange([...newFiles, ...files.slice(0, espacoRestante)]);
    };

    const removeNewFile = (index) => {
        onNewFilesChange(newFiles.filter((_, i) => i !== index));
    };

    const existingUrl = (path) => `${BACKEND_SERVER}v1/apps/${appId}/media/${encodePathSegments(path)}`;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Fotos ({totalAtual}/{IMOVEL_IMAGENS_MAX})</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {existingImages.map((img) => (
                    <Box
                        key={img.id}
                        sx={{
                            position: 'relative',
                            width: 110,
                            height: 110,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: img.principal ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.12)'
                        }}
                    >
                        <Box component="img" src={existingUrl(img.url)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        {img.principal && (
                            <Chip label="Capa" size="small" color="primary" sx={{ position: 'absolute', bottom: 4, left: 4, height: 20 }} />
                        )}
                        <Box sx={{ position: 'absolute', top: 2, right: 2, display: 'flex' }}>
                            {!img.principal && onSetPrincipal && (
                                <Tooltip title="Marcar como capa">
                                    <IconButton size="small" onClick={() => onSetPrincipal(img.id)} sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}>
                                        <IconStar size={14} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {img.principal && (
                                <IconButton size="small" disabled sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}>
                                    <IconStar size={14} color="#1976d2" fill="#1976d2" />
                                </IconButton>
                            )}
                            {onRemoveExisting && (
                                <Tooltip title="Remover">
                                    <IconButton size="small" onClick={() => onRemoveExisting(img.id)} sx={{ bgcolor: 'rgba(255,255,255,0.85)', ml: 0.5 }}>
                                        <IconTrash size={14} color="#d32f2f" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                ))}

                {newFiles.map((file, i) => (
                    <Box key={`new-${i}`} sx={{ position: 'relative', width: 110, height: 110, borderRadius: 2, overflow: 'hidden', border: '1px dashed #1976d2' }}>
                        <Box component="img" src={previews[i]} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <IconButton
                            size="small"
                            onClick={() => removeNewFile(i)}
                            sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)' }}
                        >
                            <IconTrash size={14} color="#d32f2f" />
                        </IconButton>
                    </Box>
                ))}

                {podeAdicionar && (
                    <Box
                        component="label"
                        sx={{
                            width: 110,
                            height: 110,
                            borderRadius: 2,
                            border: '1px dashed rgba(0,0,0,0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'text.secondary',
                            '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
                        }}
                    >
                        <IconPhotoPlus size={22} />
                        <Typography variant="caption" mt={0.5}>
                            Adicionar
                        </Typography>
                        <input type="file" hidden multiple accept="image/*" onChange={(e) => handleFilesSelected(e.target.files)} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ImovelPhotosEditor;
